// AppShellHost — the Context-spine write handlers (context spine step 3a/3b).
//
// Extracted verbatim from AppShellHost.tsx (C1 decomposition). The three
// Context BC write intents (introduce / drop / clear) mirroring the Context
// BC's three commands 1:1, plus their nascent-conversation twins and the
// composer's @-mention bridge. Each durable write awaits its port then
// refetches BOTH the explicit set and the ambient bloom (they always travel
// together) so the always-visible strip reflects the applied change. A nascent
// conversation has an anchor but no durable aggregate, so its grounding is held
// in `pendingGrounding` and flushed on the first send.
//
// This hook calls NO React hooks — it is a plain factory of render-local
// handlers, so its call site adds nothing to the component's hook order. It
// threads the shell state + setters it reads/writes; every async body keeps its
// sanctioned `void run()` shell impurity exactly as inline.

import * as Core from "@bilda/core";
import { Result } from "@bilda/utils";
import type { Dispatch, SetStateAction } from "react";
import type { AmbientContextItem } from "../../components/Chat/types.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type {
    ResourceId,
    ResourceRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import type { ChatCommand } from "../../modules/chat.js";
import { addedMentions, removedMentions } from "../contextMentions.js";
import type { IngestId } from "../ingest.js";
import type { AddedSource, AmbientItemDTO, AppPorts } from "../ports.js";
import { toComposerResourceRef } from "./constants.js";

export type ContextSpineDeps = {
    readonly ports: AppPorts;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly resolveAmbientItems: (
        dto: readonly AmbientItemDTO[],
    ) => readonly AmbientContextItem[];
    readonly conversationIsDurable: (conversationId: string) => boolean;
    readonly activeContextAnchor: Core.EntityRef.Ref | null;
    readonly activeConversationId: string;
    readonly composerMentions: readonly ComposerResourceRef[];
    // The chat door: the composer's mentions + a nascent conversation's
    // pending grounding live in the chat Model (S4).
    readonly sendChat: (command: ChatCommand) => void;
    readonly setActiveContextItems: Dispatch<
        SetStateAction<readonly ComposerResourceRef[]>
    >;
    readonly setActiveAmbientItems: Dispatch<
        SetStateAction<readonly AmbientContextItem[]>
    >;
};

export type ContextSpine = {
    readonly reloadActiveContext: (anchor: Core.EntityRef.Ref) => Promise<void>;
    readonly introduceIntoActiveContext: (item: ComposerResourceRef) => void;
    readonly groundAddedSource: (
        added: AddedSource,
        ingestId: IngestId,
    ) => void;
    readonly promoteAmbientItem: (item: AmbientContextItem) => void;
    readonly dropFromActiveContext: (item: ComposerResourceRef) => void;
    readonly clearActiveContext: () => void;
    readonly handleMentionsChange: (
        next: readonly ComposerResourceRef[],
    ) => void;
};

export const useContextSpine = ({
    ports,
    refFor,
    resolveAmbientItems,
    conversationIsDurable,
    activeContextAnchor,
    activeConversationId,
    composerMentions,
    sendChat,
    setActiveContextItems,
    setActiveAmbientItems,
}: ContextSpineDeps): ContextSpine => {
    // context spine step 3b: the ambient bloom is reloaded ALONGSIDE the
    // explicit set on every write below, never independently — introducing
    // a resource can make it disappear from one tier and appear in the
    // other in the same round-trip (the promote handler's whole point), so
    // the two reads always travel together here.
    const reloadActiveAmbient = async (anchor: Core.EntityRef.Ref) => {
        const r = await ports.loadAmbientContext(anchor);
        if (Result.isErr(r)) return;
        setActiveAmbientItems(resolveAmbientItems(r.value));
    };

    const reloadActiveContext = async (anchor: Core.EntityRef.Ref) => {
        const r = await ports.loadContext(anchor);
        if (Result.isErr(r)) return;
        setActiveContextItems(
            r.value.flatMap((id) => {
                const ref = refFor(id);
                return ref === undefined ? [] : [toComposerResourceRef(ref)];
            }),
        );
        await reloadActiveAmbient(anchor);
    };

    // The active conversation id when it is NASCENT (no durable aggregate yet) —
    // grounding it means updating the chat Model's `pendingGrounding`, not issuing a
    // durable `introduceToContext` (which needs a real anchor). Null when the
    // active tab is a resource, a durable conversation, or a non-anchor tab.
    const nascentActiveConversationId = (): string | null =>
        conversationIsDurable(activeConversationId)
            ? null
            : activeConversationId;

    const introduceIntoActiveContext = (item: ComposerResourceRef) => {
        // D4: grounding is {source}-STRICT. A non-source @-pick is a
        // REFERENCE only — it is tracked in `composerMentions` and stamped
        // into `resourceRefs` at send (the reference relation), but it must
        // NEVER enter the grounding channel (Context BC → Sources tier).
        // Same source-only predicate the in-card `+` door
        // (`ContextSideCard.referenceCandidatesOf`) and the app-layer gate
        // (`introduceToContext`'s `isGroundableAsSource`) apply — this UI
        // guard makes the grounding write structurally unreachable for a
        // non-source pick, the app gate is the backstop for any other caller.
        if (item.kind !== "source") return;
        const nascentId = nascentActiveConversationId();
        if (nascentId !== null) {
            // Pre-send grounding on a nascent conversation: held locally,
            // flushed to durable Context on the first send (§3.3).
            const ref = refFor(item.id);
            if (ref !== undefined)
                sendChat({
                    type: "pinned_grounding_source",
                    conversationId: nascentId,
                    source: ref,
                });
            return;
        }
        const anchor = activeContextAnchor;
        if (anchor === null) return;
        const ref = Core.Resource.Ref.mkWhole(
            Core.Resource.Id.mk(item.id),
            Core.Versioning.Version.v1,
        );
        const run = async () => {
            await ports.introduceToContext(anchor, ref);
            await reloadActiveContext(anchor);
        };
        void run();
    };

    // Auto-ground a just-added source into the active conversation so the agent
    // sees it (A-07, round-2 fix: a source added mid-conversation must land in
    // Context, not just show as a chip). A freshly-ingested source
    // is NOT in the library index yet at this callback (the library reload is
    // async), so `refFor` would MISS it and silently drop the pending grounding
    // — the exact "agent unaware" bug. We therefore build the `SourceRef`
    // directly from the AddedSource (fresh OR reused; both carry sourceId+title,
    // and a just-added source is ALWAYS a source — {source}-safe) rather than
    // routing through `introduceIntoActiveContext`. Same two-branch home as that
    // helper: nascent → held in `pendingGrounding` (flushed on first send);
    // durable → `introduceToContext` against the active anchor.
    const groundAddedSource = (added: AddedSource, ingestId: IngestId) => {
        const sourceRef: SourceRef = {
            type: "source",
            id: added.sourceId,
            title: added.title,
        };
        const nascentId = nascentActiveConversationId();
        if (nascentId !== null) {
            sendChat({
                type: "pinned_grounding_source",
                conversationId: nascentId,
                source: sourceRef,
            });
            return;
        }
        const anchor = activeContextAnchor;
        if (anchor === null) return;
        // A durable conversation: the landed file also joins the draft's
        // chips, so the next message carries it (not display-only).
        sendChat({
            type: "attached_landed_source",
            ingestId,
            source: toComposerResourceRef(sourceRef),
        });
        const ref = Core.Resource.Ref.mkWhole(
            Core.Resource.Id.mk(added.sourceId),
            Core.Versioning.Version.v1,
        );
        const run = async () => {
            await ports.introduceToContext(anchor, ref);
            await reloadActiveContext(anchor);
        };
        void run();
    };

    // Promote ONE ambient item into the explicit set — the drag-drop AND
    // the `+` button gestures both call this (ContextStrip's `onPromote`
    // prop, one handler regardless of affordance, per the design doc).
    // Reuses `introduceIntoActiveContext` verbatim: promoting IS
    // introducing, the same command, just a different gesture-origin — no
    // second write path to keep in sync.
    const promoteAmbientItem = (item: AmbientContextItem) => {
        introduceIntoActiveContext(item.resourceRef);
    };

    const dropFromActiveContext = (item: ComposerResourceRef) => {
        // Mirror of the introduce guard (D4): only a source was ever
        // grounded, so only a source can be dropped from the grounding
        // channel. A non-source removal is a reference-only concern (its
        // `composerMentions` entry is dropped by `edited_mentions`).
        if (item.kind !== "source") return;
        const nascentId = nascentActiveConversationId();
        if (nascentId !== null) {
            sendChat({
                type: "unpinned_grounding_source",
                conversationId: nascentId,
                sourceId: item.id,
            });
            return;
        }
        const anchor = activeContextAnchor;
        if (anchor === null) return;
        const ref = Core.Resource.Ref.mkWhole(
            Core.Resource.Id.mk(item.id),
            Core.Versioning.Version.v1,
        );
        const run = async () => {
            await ports.dropFromContext(anchor, ref);
            await reloadActiveContext(anchor);
        };
        void run();
    };

    const clearActiveContext = () => {
        const nascentId = nascentActiveConversationId();
        if (nascentId !== null) {
            sendChat({
                type: "cleared_grounding_sources",
                conversationId: nascentId,
            });
            return;
        }
        const anchor = activeContextAnchor;
        if (anchor === null) return;
        const run = async () => {
            await ports.clearContext(anchor);
            await reloadActiveContext(anchor);
        };
        void run();
    };

    // The composer's `@`-mention gesture, wired to the Context BC (context
    // spine step 3a): a NEWLY picked mention dispatches `introduceToContext`
    // for the active anchor immediately (durable, visible in the strip); a
    // mention removed via the composer's own inline × dispatches
    // `dropFromContext` the same way — symmetric, so the composer's chip
    // stays a live control over context, not a decorative draft-only list.
    // `composerMentions` itself keeps tracking the full picked-in-this-draft
    // set (PowerComposer's own already-mentioned de-dup filter needs it);
    // it no longer feeds `ports.sendTurn` — see that call site below.
    const handleMentionsChange = (next: readonly ComposerResourceRef[]) => {
        for (const item of addedMentions(composerMentions, next)) {
            introduceIntoActiveContext(item);
        }
        for (const item of removedMentions(composerMentions, next)) {
            dropFromActiveContext(item);
        }
        sendChat({ type: "edited_mentions", mentions: next });
    };

    return {
        reloadActiveContext,
        introduceIntoActiveContext,
        groundAddedSource,
        promoteAmbientItem,
        dropFromActiveContext,
        clearActiveContext,
        handleMentionsChange,
    };
};
